import React, { useState } from "react";
import {
  Box,
  Button,
  Card,
  CardContent,
  Grid,
  IconButton,
  Typography,
} from "@mui/material";
import GenericInput from "src/components/input-components/GenericTextInput";
import GenericTextAreaInput from "src/components/input-components/GenericTextAreaInput";
import ImagePicker from "src/components/input-components/GenericImagePicker";
import DeleteIcon from "@mui/icons-material/Delete";
import AddIcon from "@mui/icons-material/Add";

export interface IServiceV1Item {
  Title: string;
  ImageUrl: string;
  Description: string;
  LinkTitle: string;
  Link: string;
}

export interface IServicesV1Widget {
  type: string; // should be WidgetType.ServicesV1
  Title: string;
  Description: string;
  Items: IServiceV1Item[];
}

interface ServicesV1ItemProps {
  item: IServicesV1Widget;
  error: any;
  onChange: (updatedItem: IServicesV1Widget, field: string) => void;
  onblur: (field: string, index?: number) => void;
}

const ServicesV1Item: React.FC<ServicesV1ItemProps> = ({
  item,
  error,
  onChange,
  onblur,
}) => {
  // Overall widget fields
  const widgetTitle: string = typeof item.Title === "string" ? item.Title : "";
  const widgetDescription: string =
    typeof item.Description === "string" ? item.Description : "";
  const serviceItems: IServiceV1Item[] = Array.isArray(item.Items) ? item.Items : [];

  // Helper: extract value from event or use raw value.
  const getValue = (e: React.ChangeEvent<HTMLInputElement> | string): string =>
    typeof e === "string" ? e : e.target.value;

  // Handlers for overall fields.
  const handleTitleChange = (e: React.ChangeEvent<HTMLInputElement> | string) => {
    onChange({ ...item, Title: getValue(e) }, "Title");
  };

  const handleDescriptionChange = (e: React.ChangeEvent<HTMLInputElement> | string) => {
    onChange({ ...item, Description: getValue(e) }, "Description");
  };

  // Handler for updating an individual service item.
  const handleItemChange = (
    index: number,
    field: keyof IServiceV1Item,
    e: React.ChangeEvent<HTMLInputElement> | string
  ) => {
    const newValue = getValue(e);
    const updatedItems = [...serviceItems];
    updatedItems[index] = { ...updatedItems[index], [field]: newValue };
    onChange({ ...item, Items: updatedItems }, "Items");
  };

  // Add a new service item.
  const handleAddItem = () => {
    const newItem: IServiceV1Item = {
      Title: "",
      ImageUrl: "",
      Description: "",
      LinkTitle: "",
      Link: "",
    };
    const updatedItems = [...serviceItems, newItem];
    onChange({ ...item, Items: updatedItems }, "Items");
  };

  // Remove a service item.
  const handleRemoveItem = (index: number) => {
    const updatedItems = serviceItems.filter((_, i) => i !== index);
    onChange({ ...item, Items: updatedItems }, "Items");
  };

  // --- Image Picker for Service Item Image ---
  const [imagePickerOpen, setImagePickerOpen] = useState(false);
  const [currentIndex, setCurrentIndex] = useState<number | null>(null);

  const handleOpenImagePicker = (index: number) => {
    setCurrentIndex(index);
    setImagePickerOpen(true);
  };

  const handleImageSelect = (selected: string | string[]) => {
    if (currentIndex !== null) {
      const newImage = Array.isArray(selected) ? selected[0] : selected;
      const updatedItems = [...serviceItems];
      updatedItems[currentIndex] = { ...updatedItems[currentIndex], ImageUrl: newImage };
      onChange({ ...item, Items: updatedItems }, "Items");
    }
    setImagePickerOpen(false);
    setCurrentIndex(null);
  };

  return (
    <Card elevation={2} sx={{ p: 2, mb: 2 }}>
      <CardContent>
        <Typography variant="h6" gutterBottom>
          Services Widget
        </Typography>

        {/* Overall Widget Fields */}
        <GenericInput
          type="text"
          name="Title"
          title="Widget Title"
          value={widgetTitle}
          error={error?.Title || ""}
          onChange={(e) => handleTitleChange(e)}
          onBlur={() => onblur("Title")}
          disabled={false}
        />
        <GenericInput
          type="text"
          name="Description"
          title="Widget Description"
          value={widgetDescription}
          error={error?.Description || ""}
          onChange={(e) => handleDescriptionChange(e)}
          onBlur={() => onblur("Description")}
          disabled={false}
        />

        {/* Service Items */}
        <Typography variant="subtitle1" sx={{ mt: 2 }}>
          Service Items
        </Typography>
        {serviceItems.map((service, index) => (
          <Box
            key={index}
            sx={{
              border: "1px solid #ddd",
              p: 2,
              borderRadius: "4px",
              mb: 2,
            }}
          >
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <GenericInput
                  type="text"
                  name={`ServiceTitle-${index}`}
                  title="Service Title"
                  value={service.Title}
                  error={error?.[index]?.Title || ""}
                  onChange={(e) => handleItemChange(index, "Title", e)}
                  onBlur={() => onblur("Title", index)}
                  disabled={false}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                {/* Replace ImageUrl text input with an image picker */}
                <Box
                  sx={{
                    border: "1px solid #ccc",
                    borderRadius: "4px",
                    height: 100,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    cursor: "pointer",
                    backgroundColor: "#f9f9f9",
                  }}
                  onClick={() => handleOpenImagePicker(index)}
                >
                  {service.ImageUrl ? (
                    <img
                      src={service.ImageUrl}
                      alt={`Service ${index + 1}`}
                      style={{ width: "100%", height: "100%", objectFit: "cover" }}
                    />
                  ) : (
                    <Typography variant="body2" color="textSecondary">
                      Select Image
                    </Typography>
                  )}
                </Box>
                {error?.[index]?.ImageUrl && (
                  <Typography variant="body2" color="error">
                    {error?.[index]?.ImageUrl}
                  </Typography>
                )}
              </Grid>
              <Grid item xs={12}>
                <GenericTextAreaInput
                  name={`ServiceDescription-${index}`}
                  title="Service Description"
                  value={service.Description}
                  error={error?.[index]?.Description || ""}
                  onChange={(e) => handleItemChange(index, "Description", e)}
                  onBlur={() => onblur("Description", index)}
                  disabled={false}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <GenericInput
                  type="text"
                  name={`LinkTitle-${index}`}
                  title="Link Title"
                  value={service.LinkTitle}
                  error={error?.[index]?.LinkTitle || ""}
                  onChange={(e) => handleItemChange(index, "LinkTitle", e)}
                  onBlur={() => onblur("LinkTitle", index)}
                  disabled={false}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <GenericInput
                  type="text"
                  name={`Link-${index}`}
                  title="Link"
                  value={service.Link}
                  error={error?.[index]?.Link || ""}
                  onChange={(e) => handleItemChange(index, "Link", e)}
                  onBlur={() => onblur("Link", index)}
                  disabled={false}
                />
              </Grid>
              <Grid item xs={12} display="flex" justifyContent="flex-end">
                <IconButton color="error" onClick={() => handleRemoveItem(index)}>
                  <DeleteIcon />
                </IconButton>
              </Grid>
            </Grid>
          </Box>
        ))}
        <Button variant="contained" startIcon={<AddIcon />} onClick={handleAddItem}>
          Add Service Item
        </Button>
      </CardContent>
      {imagePickerOpen && (
        <ImagePicker
          open={imagePickerOpen}
          multiSelect={false}
          onClose={() => setImagePickerOpen(false)}
          onSelect={handleImageSelect}
        />
      )}
    </Card>
  );
};

export default ServicesV1Item;
